import {
  Check,
  CheckCheck,
  ChevronDown,
  Copy,
  Download,
  FileText,
  Info,
  MapPin,
  Navigation,
  Paperclip,
  Reply,
  SmilePlus,
} from "lucide-react";
import { useRef, useState, type TouchEvent } from "react";
import { toast } from "sonner";

import type { ChatMessageDto, LocationPayload } from "@mandoob/shared";

import { ImageLightbox } from "@/components/shared/image-lightbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/contexts/language";
import { hapticSelection } from "@/lib/haptics";
import { cn } from "@/lib/utils";

import { DeliveryTicks } from "./delivery-ticks";
import { EmojiPicker } from "./emoji-picker";
import { formatMessageClock, formatTemplateName } from "./lib";
import { firstUrl, LinkPreview } from "./link-preview";
import { mediaSrc } from "./media";
import { AppContextMenu, OptionsMenu, type MenuOption } from "./options-menu";
import { useLongPress } from "./use-long-press";
import { VoiceMessagePlayer } from "./voice-message-player";
import { WhatsAppText } from "./whatsapp-text";

// Tailored port of the reference message-bubble.tsx: text and template bodies
// with WhatsApp rich-text markup, timestamp + delivery ticks, failed state,
// inline media, location cards — plus the reference's reaction picker
// (long-press / hover / menu pill), message options menu (hover chevron) and
// right-click context menu, trimmed to the actions this backend supports.

const QUICK_REACTIONS = ["👍", "❤️", "🌹", "😂", "😮", "😢"];

const MEDIA_LABEL_KEYS: Partial<Record<ChatMessageDto["type"], string>> = {
  Image: "Mandoob:Message.Image",
  Video: "Mandoob:Message.Video",
  Audio: "Mandoob:Message.Audio",
  Document: "Mandoob:Message.Document",
  Sticker: "Mandoob:Message.Sticker",
  Location: "Mandoob:Message.Location",
  Contacts: "Mandoob:Message.Contacts",
};

const MEDIA_TYPES = new Set<ChatMessageDto["type"]>([
  "Image",
  "Video",
  "Audio",
  "Document",
  "Sticker",
]);

// Emoji-only messages render big and bare, like WhatsApp (reference port).
function emojiSegments(text: string): { count: number; emojiOnly: boolean } {
  const trimmed = text.trim();
  if (!trimmed || typeof Intl.Segmenter !== "function") {
    return { count: 0, emojiOnly: false };
  }
  const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
  let count = 0;
  for (const { segment } of segmenter.segment(trimmed)) {
    if (/^\s+$/u.test(segment)) continue;
    if (!/\p{Extended_Pictographic}/u.test(segment)) {
      return { count: 0, emojiOnly: false };
    }
    count++;
  }
  return { count, emojiOnly: count > 0 };
}

function emojiOnlySize(count: number): string {
  if (count <= 3) return "tw:text-[40px] tw:leading-[1.15]";
  if (count <= 6) return "tw:text-[30px] tw:leading-[1.2]";
  return "tw:text-[22px] tw:leading-[1.3]";
}

/** One-line preview of a quoted message: its text, or a localized type label. */
export function quotedPreview(
  message: ChatMessageDto,
  t: (key: string) => string,
): string {
  const labelKey = MEDIA_LABEL_KEYS[message.type];
  if (message.type === "Location" || !message.body) {
    return labelKey ? t(labelKey) : (message.body ?? "");
  }
  return message.body;
}

// Swipe-to-reply tuning (reference port): horizontal drag needed to fire a
// reply, the vertical slack beyond which the gesture is a scroll, and how far
// the bubble may follow the finger.
const SWIPE_TRIGGER_DISTANCE = 56;
const SWIPE_VERTICAL_TOLERANCE = 24;
const SWIPE_MAX_OFFSET = 72;

/**
 * WhatsApp-style swipe-to-reply. Returns the live horizontal offset (the
 * caller slides the bubble with the finger), a 0..1 progress for the reply
 * hint, and touch handlers. Disabled when `onReply` is undefined.
 */
function useSwipeReply(onReply?: () => void) {
  const startX = useRef(0);
  const startY = useRef(0);
  const tracking = useRef(false);
  const [offset, setOffset] = useState(0);

  const onTouchStart = (e: TouchEvent) => {
    if (!onReply) return;
    const touch = e.touches[0];
    startX.current = touch.clientX;
    startY.current = touch.clientY;
    tracking.current = true;
  };

  const onTouchMove = (e: TouchEvent) => {
    if (!tracking.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - startX.current;
    const dy = touch.clientY - startY.current;
    // Vertical intent wins — let the thread scroll and abandon the swipe.
    if (
      Math.abs(dy) > SWIPE_VERTICAL_TOLERANCE &&
      Math.abs(dy) > Math.abs(dx)
    ) {
      tracking.current = false;
      setOffset(0);
      return;
    }
    setOffset(Math.max(-SWIPE_MAX_OFFSET, Math.min(SWIPE_MAX_OFFSET, dx)));
  };

  const end = () => {
    if (tracking.current && Math.abs(offset) >= SWIPE_TRIGGER_DISTANCE) {
      onReply?.();
    }
    tracking.current = false;
    setOffset(0);
  };

  return {
    offset,
    progress: Math.min(1, Math.abs(offset) / SWIPE_TRIGGER_DISTANCE),
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd: end,
      onTouchCancel: end,
    },
  };
}

// Save-as filename: append the extension implied by the MIME type when the
// server-provided name has none (subset of the reference's map).
const MIME_EXTENSIONS: Record<string, string> = {
  "image/jpeg": "jpg",
  "audio/mpeg": "mp3",
  "audio/ogg": "ogg",
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "text/plain": "txt",
};

function downloadFileName(
  fileName: string | null | undefined,
  mime?: string | null,
): string {
  const base = fileName?.trim();
  if (base && /\.[a-z0-9]+$/i.test(base)) return base;
  const type = mime?.split(";")[0].trim().toLowerCase() ?? "";
  const subtype = type.split("/")[1];
  const ext =
    MIME_EXTENSIONS[type] ??
    (subtype && /^[a-z0-9]+$/.test(subtype) ? subtype : null);
  const stem = base || "download";
  return ext ? `${stem}.${ext}` : stem;
}

// Blob round-trip so the `download` filename sticks (see reference notes).
function downloadMedia(message: ChatMessageDto): void {
  if (!message.mediaUrl) return;
  fetch(mediaSrc(message.mediaUrl))
    .then((res) => res.blob())
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = downloadFileName(
        message.mediaFileName,
        blob.type || message.mediaMimeType,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    })
    .catch(() => undefined);
}

function MediaContent({ message }: { message: ChatMessageDto }) {
  const { t } = useLanguage();
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!message.mediaUrl) return null;
  const url = mediaSrc(message.mediaUrl);

  // Stickers render small and bare, like the reference — no lightbox, no
  // cover-crop, just the webp at its own aspect.
  if (message.type === "Sticker") {
    return (
      <img
        src={url}
        alt={message.mediaFileName ?? ""}
        loading="lazy"
        className="tw:max-h-[100px] tw:max-w-[100px] tw:object-contain"
      />
    );
  }

  if (message.type === "Image") {
    return (
      <>
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="tw:mb-1.5 tw:block tw:cursor-zoom-in tw:overflow-hidden tw:rounded-md"
        >
          <img
            src={url}
            alt={message.mediaFileName ?? ""}
            loading="lazy"
            className="tw:max-h-64 tw:w-full tw:max-w-[280px] tw:rounded-md tw:object-cover"
          />
        </button>
        <ImageLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          src={url}
          alt={message.mediaFileName ?? undefined}
        />
      </>
    );
  }

  if (message.type === "Video") {
    return (
      <video
        src={url}
        controls
        preload="metadata"
        className="tw:mb-1 tw:max-h-64 tw:w-full tw:max-w-[280px] tw:rounded-md"
      />
    );
  }

  if (message.type === "Audio") {
    return <VoiceMessagePlayer url={url} />;
  }

  // Document (and any other media the backend proxies): a download card.
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="tw:mb-1.5 tw:flex tw:w-full tw:items-center tw:gap-2.5 tw:rounded-xl tw:border tw:border-border tw:bg-card tw:px-3 tw:py-2.5 tw:text-start tw:no-underline"
    >
      <FileText
        className="tw:size-6 tw:shrink-0 tw:text-muted-foreground"
        strokeWidth={1.75}
      />
      <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm tw:font-medium tw:text-foreground">
        {message.mediaFileName ?? t("Mandoob:Message.Document")}
      </span>
      <Download
        className="tw:size-4 tw:shrink-0 tw:text-muted-foreground"
        strokeWidth={1.75}
      />
    </a>
  );
}

function parseLocation(body: string | null): LocationPayload | null {
  if (!body) return null;
  try {
    const parsed = JSON.parse(body) as LocationPayload;
    if (
      typeof parsed?.latitude !== "number" ||
      typeof parsed?.longitude !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// Lightweight, keyless location thumbnail stitched from official
// OpenStreetMap tiles, with the marker pinned at the exact coordinates
// (reference OsmMapPreview port).
const TILE_SIZE = 256;
const PREVIEW_ZOOM = 15;
const PREVIEW_WIDTH = 240;
const PREVIEW_HEIGHT = 112;

function OsmMapPreview({
  latitude,
  longitude,
  label,
  onError,
}: {
  latitude: number;
  longitude: number;
  label: string;
  onError: () => void;
}) {
  const n = 2 ** PREVIEW_ZOOM;
  const latRad = (latitude * Math.PI) / 180;
  const xF = ((longitude + 180) / 360) * n;
  const yF =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  const xTile = Math.floor(xF);
  const yTile = Math.floor(yF);
  // Shift a 3x3 tile grid so the exact point lands at the preview center.
  const offsetX = PREVIEW_WIDTH / 2 - (TILE_SIZE + (xF - xTile) * TILE_SIZE);
  const offsetY = PREVIEW_HEIGHT / 2 - (TILE_SIZE + (yF - yTile) * TILE_SIZE);

  const deltas = [-1, 0, 1];

  return (
    <div
      dir="ltr"
      className="tw:relative tw:h-28 tw:w-full tw:overflow-hidden tw:bg-muted"
    >
      <div
        className="tw:absolute tw:left-0 tw:top-0"
        style={{ transform: `translate(${offsetX}px, ${offsetY}px)` }}
      >
        {deltas.map((dy) =>
          deltas.map((dx) => (
            <img
              key={`${dx}_${dy}`}
              src={`https://tile.openstreetmap.org/${PREVIEW_ZOOM}/${xTile + dx}/${yTile + dy}.png`}
              alt=""
              loading="lazy"
              onError={onError}
              className="tw:absolute tw:max-w-none"
              style={{
                width: TILE_SIZE,
                height: TILE_SIZE,
                left: (dx + 1) * TILE_SIZE,
                top: (dy + 1) * TILE_SIZE,
              }}
            />
          )),
        )}
      </div>
      <MapPin
        className="tw:absolute tw:left-1/2 tw:top-1/2 tw:size-7 tw:-translate-x-1/2 tw:-translate-y-full tw:text-brand tw:drop-shadow"
        strokeWidth={2}
        fill="currentColor"
        aria-label={label}
      />
    </div>
  );
}

function LocationContent({ body }: { body: string | null }) {
  const { t } = useLanguage();
  const [previewFailed, setPreviewFailed] = useState(false);
  const location = parseLocation(body);
  if (!location) return null;

  const { latitude, longitude, name, address } = location;
  const label = name || t("Mandoob:Message.Location");
  const googleUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  const appleUrl = `https://maps.apple.com/?ll=${latitude},${longitude}&q=${encodeURIComponent(label)}`;
  // geo: pops the OS app chooser on Android; a no-op on most desktops.
  const geoUrl = `geo:${latitude},${longitude}?q=${latitude},${longitude}${
    name ? `(${encodeURIComponent(name)})` : ""
  }`;

  return (
    <div className="tw:mb-1 tw:w-60 tw:max-w-full tw:overflow-hidden tw:rounded-lg tw:border tw:border-border">
      <a
        href={googleUrl}
        target="_blank"
        rel="noreferrer"
        className="tw:block tw:no-underline"
      >
        {!previewFailed ? (
          <OsmMapPreview
            latitude={latitude}
            longitude={longitude}
            label={label}
            onError={() => setPreviewFailed(true)}
          />
        ) : (
          <div className="tw:flex tw:h-28 tw:items-center tw:justify-center tw:bg-muted">
            <MapPin
              className="tw:size-9 tw:text-brand"
              strokeWidth={2}
              fill="currentColor"
            />
          </div>
        )}
        <div className="tw:flex tw:flex-col tw:gap-0.5 tw:bg-card tw:px-2.5 tw:py-2">
          <span className="tw:truncate tw:text-sm tw:font-semibold tw:text-foreground">
            {label}
          </span>
          {address ? (
            <span className="tw:truncate tw:text-xs tw:text-muted-foreground">
              {address}
            </span>
          ) : (
            <span
              dir="ltr"
              className="tw:text-start tw:text-xs tw:tabular-nums tw:text-muted-foreground"
            >
              {latitude.toFixed(5)}, {longitude.toFixed(5)}
            </span>
          )}
        </div>
      </a>
      {/* Chooser for the driver's preferred maps app. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(event) => event.stopPropagation()}
            className="tw:flex tw:w-full tw:items-center tw:justify-center tw:gap-1.5 tw:border-t tw:border-border tw:bg-card tw:py-2 tw:text-sm tw:font-semibold tw:text-brand tw:transition-colors tw:hover:bg-muted"
          >
            <Navigation className="tw:size-4" strokeWidth={2} />
            {t("Mandoob:Location.OpenInMaps")}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="tw:w-52">
          <DropdownMenuItem
            onSelect={() => window.open(googleUrl, "_blank", "noopener")}
          >
            {t("Mandoob:Location.GoogleMaps")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => window.open(appleUrl, "_blank", "noopener")}
          >
            {t("Mandoob:Location.AppleMaps")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              window.location.href = geoUrl;
            }}
          >
            {t("Mandoob:Location.DefaultMapsApp")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function MessageBubble({
  message,
  onReact,
  onReply,
  contactName,
  quoted,
  onQuoteClick,
  highlighted,
}: {
  message: ChatMessageDto;
  /** Set/replace/remove ("" emoji) the driver's reaction on this message. */
  onReact?: (message: ChatMessageDto, emoji: string) => void;
  /** Start a quoted reply to this message. */
  onReply?: (message: ChatMessageDto) => void;
  /** Client display name, for the quoted block of inbound messages. */
  contactName?: string;
  /** The message this one replies to, resolved by the thread (if loaded). */
  quoted?: ChatMessageDto | null;
  /** Scrolls the thread to the quoted message when its preview is tapped. */
  onQuoteClick?: (messageId: string) => void;
  /** Briefly flashes this row when the thread jumps to it from a quote. */
  highlighted?: boolean;
}) {
  const { t, dir, language } = useLanguage();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Only messages that reached WhatsApp can be quoted.
  const canReply = !!onReply && !!message.wamid;
  // Reply and briefly flash the row — the double-click's visual ack.
  const replyWithFlash = () => {
    if (!canReply) return;
    onReply!(message);
    setFlash(true);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(false), 450);
  };
  const outbound = message.direction === "Outbound";
  const failed = message.delivery === "Failed";
  const isLocation = message.type === "Location";
  const isMedia = MEDIA_TYPES.has(message.type);
  const showMedia = isMedia && !!message.mediaUrl;
  const mediaLabelKey = MEDIA_LABEL_KEYS[message.type];
  const location = isLocation ? parseLocation(message.body) : null;
  const showLabelRow =
    (isMedia && !showMedia) ||
    (isLocation && !location) ||
    message.type === "Contacts";
  const isText = !isMedia && !isLocation && !!message.body;
  const { count: emojiCount, emojiOnly } = emojiSegments(message.body ?? "");
  // First URL in a plain-text message gets an OG-metadata preview card.
  const linkUrl = isText && !emojiOnly ? firstUrl(message.body ?? "") : null;

  // Only messages that reached WhatsApp can be reacted to.
  const canReact = !!onReact && !!message.wamid;
  const react = canReact
    ? (emoji: string) => onReact!(message, emoji)
    : undefined;
  const myReaction = message.driverReaction;
  const reactionPills = [
    ...(message.clientReaction
      ? [{ key: "client", emoji: message.clientReaction, mine: false }]
      : []),
    ...(myReaction ? [{ key: "driver", emoji: myReaction, mine: true }] : []),
  ];

  // Long-press opens the mobile overlay (reactions + context menu) for every
  // message — the menu always has at least "message info" to offer.
  const longPress = useLongPress(() => {
    hapticSelection();
    setPickerOpen(true);
  });
  const swipe = useSwipeReply(canReply ? () => onReply!(message) : undefined);
  // Long-press (reactions) and swipe (reply) both listen on the row's touch
  // events; a swipe moves the finger, which clears the long-press timer — so
  // they never fire together.
  const touchHandlers = {
    onTouchStart: (e: TouchEvent) => {
      longPress.onTouchStart();
      swipe.handlers.onTouchStart(e);
    },
    onTouchMove: (e: TouchEvent) => {
      longPress.onTouchMove();
      swipe.handlers.onTouchMove(e);
    },
    onTouchEnd: () => {
      longPress.onTouchEnd();
      swipe.handlers.onTouchEnd();
    },
    onTouchCancel: () => {
      longPress.onTouchCancel();
      swipe.handlers.onTouchCancel();
    },
  };

  const reactionButtons = QUICK_REACTIONS.map((emoji) => {
    const selected = myReaction === emoji;
    return (
      <button
        key={emoji}
        type="button"
        // One reaction per driver: a new emoji replaces it; re-picking the
        // selected one (empty emoji) removes it.
        onClick={() => {
          react?.(selected ? "" : emoji);
          setPickerOpen(false);
        }}
        className={cn(
          "tw:flex tw:size-12 tw:items-center tw:justify-center tw:rounded-full tw:text-3xl tw:leading-none tw:hover:bg-muted",
          selected && "tw:bg-muted",
        )}
      >
        {emoji}
      </button>
    );
  });

  const morePicker = react ? (
    <EmojiPicker
      onSelect={(emoji) => {
        react(emoji);
        setPickerOpen(false);
      }}
    >
      <button
        type="button"
        aria-label={t("Mandoob:Message.React")}
        className="tw:flex tw:size-12 tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground tw:hover:bg-muted"
      >
        <SmilePlus className="tw:size-6" strokeWidth={2} />
      </button>
    </EmojiPicker>
  ) : null;

  const pickerPanelClass =
    "tw:absolute tw:bottom-full tw:left-1/2 tw:z-50 tw:mb-2 tw:-translate-x-1/2 tw:items-center tw:gap-0.5 tw:rounded-full tw:border tw:border-border tw:bg-popover tw:px-1.5 tw:py-1 tw:shadow-md tw:animate-in tw:fade-in-0 tw:zoom-in-95";

  // Floating panels start centered on their anchor; when that would overflow
  // the thread pane (the scroll container clips horizontal overhang, so the
  // excess would vanish under the conversations aside), measure and shift the
  // panel back fully into view.
  const clampPanel = (el: HTMLDivElement | null) => {
    if (!el) return;
    requestAnimationFrame(() => {
      const bound = (
        el.closest(".chat-wallpaper") ?? document.documentElement
      ).getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      const pad = 8;
      let dx = 0;
      if (rect.left < bound.left + pad) dx = bound.left + pad - rect.left;
      else if (rect.right > bound.right - pad)
        dx = bound.right - pad - rect.right;
      // Tailwind v4's -translate-x-1/2 lives on the standalone `translate`
      // property — override THAT (keeping the -50% baseline), never
      // `transform`, or the two would stack and shift the panel a full
      // half-width off while the zoom-in animation plays.
      if (dx !== 0) el.style.translate = `calc(-50% + ${dx}px)`;
    });
  };

  // Desktop: hover icon beside the bubble, quick pill anchored above it.
  const trigger = react ? (
    <div className="tw:relative tw:hidden tw:md:block">
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        aria-label={t("Mandoob:Message.React")}
        className="tw:flex tw:size-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-card tw:opacity-0 tw:shadow-sm tw:transition-opacity tw:focus-visible:opacity-100 tw:group-hover/msg:opacity-100"
      >
        <SmilePlus
          className="tw:size-5 tw:text-muted-foreground"
          strokeWidth={2}
        />
      </button>
      {pickerOpen ? (
        <div
          ref={clampPanel}
          className={cn(pickerPanelClass, "tw:hidden tw:md:flex")}
        >
          {reactionButtons}
          {morePicker}
        </div>
      ) : null}
    </div>
  ) : null;

  const menuOptions: MenuOption[] = [
    {
      type: "item",
      label: t("Mandoob:Message.Info"),
      icon: Info,
      onClick: () => setInfoOpen(true),
    },
    ...(canReply
      ? [
          {
            type: "item" as const,
            label: t("Mandoob:Message.Reply"),
            icon: Reply,
            onClick: () => onReply!(message),
          },
        ]
      : []),
    ...(isText
      ? [
          {
            type: "item" as const,
            label: t("Mandoob:Message.Copy"),
            icon: Copy,
            onClick: () => {
              navigator.clipboard
                ?.writeText(message.body ?? "")
                .then(() => toast.success(t("Mandoob:Message.Copied")))
                .catch(() => undefined);
            },
          },
        ]
      : []),
    // Stickers aren't meant to be saved (reference behavior).
    ...(showMedia && message.type !== "Sticker"
      ? [
          {
            type: "item" as const,
            label: t("Mandoob:Message.Download"),
            icon: Download,
            onClick: () => downloadMedia(message),
          },
        ]
      : []),
  ];

  // Mobile: long-press the row → WhatsApp-style overlay with the reaction
  // pill above the row and the context menu below it; tap-away to close.
  const mobileOverlay = pickerOpen ? (
    <>
      <button
        type="button"
        aria-label={t("Mandoob:Back")}
        onClick={() => setPickerOpen(false)}
        className="tw:fixed tw:inset-0 tw:z-40 tw:cursor-default"
      />
      {react ? (
        <div
          ref={clampPanel}
          className={cn(pickerPanelClass, "tw:flex tw:md:hidden")}
        >
          {reactionButtons}
          {morePicker}
        </div>
      ) : null}
      <div
        ref={clampPanel}
        className="tw:absolute tw:left-1/2 tw:top-full tw:z-50 tw:mt-1.5 tw:w-48 tw:-translate-x-1/2 tw:rounded-xl tw:border tw:border-border tw:bg-popover tw:p-1 tw:shadow-md tw:animate-in tw:fade-in-0 tw:zoom-in-95 tw:md:hidden"
      >
        {menuOptions.map((option, index) => {
          if (option.type === "separator") {
            return <div key={index} className="tw:my-1 tw:h-px tw:bg-border" />;
          }
          const Icon = option.icon;
          return (
            <button
              key={index}
              type="button"
              onClick={() => {
                setPickerOpen(false);
                option.onClick();
              }}
              className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-lg tw:px-2.5 tw:py-2 tw:text-start tw:text-sm tw:text-foreground tw:active:bg-muted"
            >
              {Icon && (
                <Icon
                  className={cn("tw:size-4.5", option.iconClassName)}
                  strokeWidth={1.75}
                />
              )}
              {option.label}
            </button>
          );
        })}
      </div>
    </>
  ) : null;

  // Top-end corner chevron revealing the options menu on hover.
  const optionsMenu = (
    <OptionsMenu
      options={menuOptions}
      align="end"
      reactionEmojis={react ? QUICK_REACTIONS : undefined}
      reactions={myReaction ? [myReaction] : []}
      onReact={react}
    >
      <button
        type="button"
        aria-label={t("Mandoob:Message.Menu")}
        className="chat-bubble-arrow tw:absolute tw:end-0 tw:top-0 tw:z-1 tw:flex tw:size-6 tw:items-center tw:justify-center tw:opacity-0 tw:transition-opacity tw:focus-visible:opacity-100 tw:group-hover/msg:opacity-100 tw:data-[state=open]:opacity-100"
      >
        <ChevronDown
          className="tw:size-5 tw:text-muted-foreground"
          strokeWidth={2}
        />
      </button>
    </OptionsMenu>
  );

  const locale = language === "ar" ? "ar-EH" : "en-US";
  const formatDateTime = (unixSeconds: number) =>
    new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(new Date(unixSeconds * 1000));
  const sentAt =
    typeof message.waTimestamp === "number"
      ? formatDateTime(message.waTimestamp)
      : "—";
  const deliveredAt =
    typeof message.deliveryTime === "number"
      ? formatDateTime(message.deliveryTime)
      : null;
  const readAt =
    typeof message.readTime === "number"
      ? formatDateTime(message.readTime)
      : null;

  const infoRow = (
    icon: React.ReactNode,
    label: string,
    value: string | null,
    missingKey: string,
  ) => (
    <div className="tw:flex tw:items-center tw:justify-between tw:gap-4">
      <dt className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground">
        {icon}
        {label}
      </dt>
      <dd className="tw:m-0 tw:text-sm tw:font-medium tw:tabular-nums">
        {value ?? (
          <span className="tw:font-normal tw:text-muted-foreground">
            {t(missingKey)}
          </span>
        )}
      </dd>
    </div>
  );

  const infoDialog = (
    <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
      <DialogContent className="tw:max-w-xs">
        <DialogHeader>
          <DialogTitle>{t("Mandoob:Message.Info")}</DialogTitle>
        </DialogHeader>
        <dl className="tw:m-0 tw:flex tw:flex-col tw:gap-3">
          {infoRow(
            <Check className="tw:size-4" strokeWidth={2} />,
            t("Mandoob:Message.SentAt"),
            sentAt,
            "Mandoob:Message.NotDeliveredYet",
          )}
          {outbound
            ? infoRow(
                <CheckCheck className="tw:size-4" strokeWidth={2} />,
                t("Mandoob:Message.DeliveredAt"),
                deliveredAt,
                "Mandoob:Message.NotDeliveredYet",
              )
            : null}
          {outbound
            ? infoRow(
                <CheckCheck
                  className="tw:size-4 tw:text-[#007BFC]"
                  strokeWidth={2}
                />,
                t("Mandoob:Message.ReadAt"),
                readAt,
                "Mandoob:Message.NotReadYet",
              )
            : null}
        </dl>
      </DialogContent>
    </Dialog>
  );

  return (
    <div
      {...touchHandlers}
      // Double-click to reply, but only on the row's own area (the gutter
      // beside the bubble) — not on the bubble, so double-clicking text to
      // select it still works.
      onDoubleClick={
        canReply
          ? (event) => {
              if (event.target === event.currentTarget) replyWithFlash();
            }
          : undefined
      }
      // Long-press must open OUR overlay, never the browser's own context
      // menu (Android long-press). Desktop right-clicks on the bubble are
      // handled (and stopped) by AppContextMenu before reaching here.
      onContextMenu={(event) => event.preventDefault()}
      className={cn(
        // Horizontal padding lives here (not on the thread) so the selected
        // row highlight spans edge-to-edge while the content stays inset.
        "tw:group/msg tw:relative tw:flex tw:w-full tw:touch-pan-y tw:items-center tw:gap-2 tw:px-4 tw:transition-colors tw:pointer-coarse:select-none tw:md:px-6 tw:[-webkit-touch-callout:none]",
        outbound ? "tw:justify-end" : "tw:justify-start",
        // Green translucent highlight while the mobile long-press overlay is
        // open (WhatsApp-style selected row).
        pickerOpen && "tw:bg-brand/15 tw:md:bg-transparent",
        // Brief flash acknowledging a double-click reply / a quote jump —
        // same translucent green as the long-press highlight.
        (flash || highlighted) && "tw:bg-brand/15",
      )}
    >
      {infoDialog}
      {mobileOverlay}
      {swipe.offset !== 0 ? (
        <span
          aria-hidden
          style={{ opacity: swipe.progress }}
          className={cn(
            // Physical left/right (not logical start/end) so the hint tracks
            // the finger's actual direction in both LTR and RTL layouts.
            "tw:pointer-events-none tw:absolute tw:top-1/2 tw:flex tw:size-8 tw:-translate-y-1/2 tw:items-center tw:justify-center tw:rounded-full tw:bg-muted tw:text-muted-foreground",
            swipe.offset > 0 ? "tw:left-1" : "tw:right-1",
          )}
        >
          <Reply className="tw:size-4" strokeWidth={2} />
        </span>
      ) : null}
      {outbound ? trigger : null}
      <AppContextMenu
        options={menuOptions}
        reactionEmojis={react ? QUICK_REACTIONS : undefined}
        reactions={myReaction ? [myReaction] : []}
        onReact={react}
      >
        <div
          style={
            swipe.offset
              ? { transform: `translateX(${swipe.offset}px)` }
              : undefined
          }
          className={cn(
            // chat-bubble carries the surface color, subtle shadow and the
            // WhatsApp corner tail (::after) — see main.css. The tail sits at
            // the top outer corner, which stays square like the reference.
            "chat-bubble tw:relative tw:max-w-[85%] tw:rounded-lg tw:px-2.5 tw:py-1.5",
            outbound ? "chat-bubble-out" : "chat-bubble-in",
            failed && "tw:border tw:border-destructive/40",
            reactionPills.length > 0 && "tw:mb-5",
            // Snap back after release; while dragging the transform follows
            // the finger with no transition.
            swipe.offset === 0 && "tw:transition-transform",
          )}
        >
          {optionsMenu}
          {quoted ? (
            // Accent reflects who sent the quoted message: brand when it's
            // ours, blue (read-tick tone) when it's the client's. Tapping it
            // scrolls the thread to the quoted message.
            <button
              type="button"
              disabled={!onQuoteClick}
              onClick={() => onQuoteClick?.(quoted.id)}
              className={cn(
                // min-width keeps the quote card readable even when the reply
                // itself is a couple of characters.
                "tw:mb-1 tw:flex tw:w-full tw:min-w-44 tw:flex-col tw:gap-0.5 tw:overflow-hidden tw:rounded-md tw:border-s-4 tw:bg-black/[0.05] tw:px-2 tw:py-1 tw:text-start tw:dark:bg-white/[0.06]",
                onQuoteClick && "tw:cursor-pointer",
                quoted.direction === "Outbound"
                  ? "tw:border-brand"
                  : "tw:border-[#007BFC]",
              )}
            >
              <span
                className={cn(
                  "tw:truncate tw:text-xs tw:font-semibold",
                  quoted.direction === "Outbound"
                    ? "tw:text-brand"
                    : "tw:text-[#007BFC]",
                )}
              >
                {quoted.direction === "Outbound"
                  ? t("Mandoob:Message.You")
                  : contactName}
              </span>
              <span className="tw:line-clamp-2 tw:whitespace-pre-wrap tw:break-words tw:text-[13px] tw:leading-[17px] tw:text-muted-foreground">
                {quotedPreview(quoted, t)}
              </span>
            </button>
          ) : null}
          {message.type === "Template" && (
            <div className="tw:mb-1 tw:flex tw:items-center tw:gap-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
              <FileText className="tw:size-3.5" strokeWidth={1.75} />
              {message.templateName
                ? formatTemplateName(message.templateName)
                : t("Mandoob:Message.Template")}
            </div>
          )}

          {showMedia && <MediaContent message={message} />}
          {isLocation && location && <LocationContent body={message.body} />}

          {showLabelRow && mediaLabelKey && (
            <div className="tw:mb-1 tw:flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-muted-foreground">
              <Paperclip className="tw:size-4" strokeWidth={1.75} />
              <span>{t(mediaLabelKey)}</span>
            </div>
          )}

          {linkUrl ? <LinkPreview url={linkUrl} /> : null}

          {message.body && !isLocation && (
            <p
              className={cn(
                "tw:m-0 tw:whitespace-pre-wrap tw:break-words tw:text-foreground",
                emojiOnly && isText
                  ? emojiOnlySize(emojiCount)
                  : "tw:text-base",
              )}
            >
              {emojiOnly && isText ? (
                message.body
              ) : (
                <WhatsAppText text={message.body} />
              )}
            </p>
          )}

          {failed && (
            <p className="tw:m-0 tw:mt-1 tw:text-xs tw:font-medium tw:text-destructive">
              {t("Mandoob:Message.Failed")}
              {message.errorCode ? ` (${message.errorCode})` : ""}
            </p>
          )}

          <div className="tw:mt-1 tw:flex tw:items-center tw:justify-end tw:gap-1">
            <span
              // App direction, like the reference: in Arabic the day-period
              // marker (م/ص) sits on the start side of the digits — "9:49 م".
              dir={dir}
              className="tw:text-[11px] tw:leading-none tw:tabular-nums tw:text-muted-foreground"
            >
              {formatMessageClock(
                message.waTimestamp,
                language,
                message.creationTime,
              )}
            </span>
            {outbound && <DeliveryTicks delivery={message.delivery} />}
          </div>

          {reactionPills.length > 0 ? (
            <div
              className={cn(
                "tw:absolute tw:-bottom-4.5 tw:z-1 tw:flex tw:items-center tw:gap-1",
                outbound ? "tw:end-2" : "tw:start-2",
              )}
            >
              {reactionPills.map((pill) => (
                <button
                  key={pill.key}
                  type="button"
                  disabled={!react}
                  onClick={() => setPickerOpen(true)}
                  aria-label={t("Mandoob:Message.React")}
                  className={cn(
                    "tw:z-1 tw:flex tw:items-center tw:rounded-full tw:border tw:border-border tw:px-1.5 tw:py-0.5 tw:text-[16px] tw:leading-none tw:shadow-sm",
                    pill.mine ? "tw:bg-bubble-out" : "tw:bg-bubble-in",
                    react && "tw:cursor-pointer",
                  )}
                >
                  {pill.emoji}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </AppContextMenu>
      {!outbound ? trigger : null}
    </div>
  );
}
